import { type ReactNode } from 'react'
import { DateTime } from 'luxon'
import {
  isDefined,
  type ReportColumnDefinition,
  type ReportColumnValue,
  type ReportKpiGrouping,
  type ReportTimePeriod,
} from '@terros/common'
import { Text } from '@mantine/core'
import { formatReportValue, getInitialism } from '@front/model'
import { PrefetchedUserInfo, PrefetchedTeamInfo, PrefetchedCompanyInfo } from '@components'

type CellProps = {
  columnDef: ReportColumnDefinition
  value: ReportColumnValue
  grouping: ReportKpiGrouping
}
export const DisplayReportColumnValue = ({ columnDef, value, grouping }: CellProps): ReactNode => {
  switch (columnDef.type) {
    case 'group': {
      if (typeof value === 'object' && isDefined(value)) {
        if ('userId' in value) return <PrefetchedUserInfo linked={false} user={value} />
        if ('teamId' in value) return <PrefetchedTeamInfo linked={false} team={value} />
        if ('companyId' in value)
          return (
            <PrefetchedCompanyInfo {...value} initials={getInitialism(value.name)} linked={false} name={value.name} />
          )
        if ('taskId' in value) {
          return (
            <Text size='sm'>
              {value.name}
            </Text>
          )
        }
      } else if (grouping.type === 'time' && typeof value === 'number')
        return <DisplayKpiTimePeriod period={grouping.period} startTime={value} />
      return value
    }
    default:
      return formatReportValue(columnDef.type, value, columnDef.formatters)
  }
}

const DisplayKpiTimePeriod = ({ startTime, period }: { startTime: number; period: ReportTimePeriod }): ReactNode => {
  return <Text size='sm'>{toDisplayKpiTimePeriod({ startTime, period })}</Text>
}

type DisplayKpiTimePeriodInput = {
  startTime: number
  period: ReportTimePeriod
}

export const toDisplayKpiTimePeriod = ({ startTime, period }: DisplayKpiTimePeriodInput): string => {
  const start = DateTime.fromMillis(startTime)
  const end = start.plus({ [period]: 1 })
  return `${start.toLocaleString(DateTime.DATETIME_SHORT)} - ${end.toLocaleString(DateTime.DATETIME_SHORT)}`
}
